import {
  DbuiV1GetDataSourceSummaryData,
  dbuiV1GetDataSourceSummaryOptions,
  dbuiV1GetDataSourceSummaryQueryKey,
  DbuiV1GetDataSourceSummaryResponse,
} from '@antimatterhq/asl-api-ts-v1';
import { useQueryClient } from '@tanstack/react-query';
import { useSuspenseQuery } from '@antimatterhq/ui/hooks/queries/useSuspenseQuery';
import { useValueWithSuspense } from '@antimatterhq/ui/hooks/suspense/useValueWithSuspense';
import { useQuery } from '@antimatterhq/ui/hooks/queries/useQuery';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { defaultOptions } from '@/utils/query';
import { useWarehouseLocation } from '@/contexts/WarehouseLocationContext';

type GetDataSourceSummaryQuery = Omit<
  NonNullable<DbuiV1GetDataSourceSummaryData['query']>,
  'warehouse'
>;

export const useGetDatasourcesSummary = (
  query: GetDataSourceSummaryQuery = {},
) => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();

  return useQuery({
    ...dbuiV1GetDataSourceSummaryOptions({
      ...defaultOptions(),
      path: { workspace },
      query: { ...query, warehouse: selectedWarehouse?.id || '' },
    }),
    refetchOnMount: true,
  });
};

export const useGetDatasourcesSummaryWithSuspense = (
  query: GetDataSourceSummaryQuery = {},
) => {
  const workspaceContext = useWorkspace();
  const warehouseLocationContext = useWarehouseLocation();

  const workspace = useValueWithSuspense(workspaceContext.workspace);
  const selectedWarehouse = useValueWithSuspense(
    warehouseLocationContext.selectedWarehouse,
  );

  return useSuspenseQuery({
    ...dbuiV1GetDataSourceSummaryOptions({
      ...defaultOptions(),
      path: { workspace },
      query: { ...query, warehouse: selectedWarehouse.id || '' },
    }),
    refetchOnMount: true,
  });
};

export const useOptimisticDeleteDataSourceFromSummaryList = (
  query: GetDataSourceSummaryQuery = {},
) => {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();

  return (
    dataSource: NonNullable<
      DbuiV1GetDataSourceSummaryResponse['dataSources']
    >[number],
  ) => {
    queryClient.setQueryData(
      dbuiV1GetDataSourceSummaryQueryKey({
        ...defaultOptions(),
        path: { workspace },
        query: { ...query, warehouse: selectedWarehouse?.id || '' },
      }),
      (prev: DbuiV1GetDataSourceSummaryResponse) => {
        return prev
          ? {
              ...prev,
              dataSources: prev.dataSources?.filter(
                (ds) => ds.name !== dataSource.name,
              ),
            }
          : prev;
      },
    );
  };
};
